// Ported from dev-scrips-pm-react src/billing/lib/management-discount-validation.ts (PROD-3312 appointment Billing tab).
/**
 * Management-discount amount validation (PROD-1755, revised by PROD-1794 §C).
 *
 * The React dialog originally enforced only "> 0 and ≤ 100" for percentages and
 * nothing against the invoice total, so a 50% invoice-wide discount was
 * submittable with no warning. PROD-1755 added both messages — but ALSO disabled
 * Apply (a hard block).
 *
 * PROD-1794 §C: the Flutter source
 * (`scrips_msp1_flutter_shared/.../add_management_discount_popup.dart`) shows
 * both messages as ADVISORY only — the apply/check button's enablement
 * (`isEnable = state.status`) depends solely on amount>0 + reason + type, and is
 * independent of the 30% / patient-payable checks (the messages at :621-645 are
 * pure display). So React now matches Flutter: the two policy messages are shown
 * but do NOT disable Apply. Only a non-positive amount blocks submission.
 *
 * Error strings are copied VERBATIM from the Flutter constants per the CLAUDE.md
 * magic-string rule — do not paraphrase.
 */

/** Flutter caps an invoice-wide management discount at 30% (percentage mode). */
export const MAX_DISCOUNT_PCT = 30;

export interface DiscountAmountValidation {
  parsed: number;
  /** Apply-button enablement — a positive amount only (reason/type gated by the caller). */
  isValid: boolean;
  /** Message to surface. May be ADVISORY (present even when `isValid` is true). */
  error?: string;
  /** True when `error` is advisory (over-cap / over-payable) rather than blocking. */
  advisory: boolean;
}

/**
 * Validate the entered discount amount for the given mode.
 *   - Blocking: amount must be a positive number, else Apply is disabled.
 *   - Advisory (Apply stays enabled, Flutter parity — PROD-1794 §C):
 *       `Pct` > 30 → "Maximum allowed discount is 30%."
 *       `Amt` > patient payable → "Amount should be less than or equal to patient payable."
 * A blank field is treated as pristine (no error, not valid).
 */
export function validateDiscountAmount(
  type: 'Amt' | 'Pct',
  rawAmount: string,
  patientPayable?: number,
): DiscountAmountValidation {
  const parsed = Number.parseFloat(rawAmount);
  const isPositiveNumber = !Number.isNaN(parsed) && parsed > 0;
  const overPctCap = type === 'Pct' && isPositiveNumber && parsed > MAX_DISCOUNT_PCT;
  const overPayable =
    type === 'Amt' &&
    isPositiveNumber &&
    patientPayable != null &&
    parsed > patientPayable;
  // Only a non-positive amount blocks Apply; the caps are advisory (match Flutter).
  const isValid = isPositiveNumber;
  const advisory = overPctCap || overPayable;
  const error =
    rawAmount && !isPositiveNumber
      ? type === 'Pct'
        ? 'Percentage must be > 0.'
        : 'Amount must be > 0.'
      : overPctCap
        ? 'Maximum allowed discount is 30%.'
        : overPayable
          ? 'Amount should be less than or equal to patient payable.'
          : undefined;
  return { parsed, isValid, error, advisory };
}
